import ComingSoon from '@components/ComingSoon'
import Error from '@components/Error'
import ErrorPlaceholder from '@components/ErrorPlaceholder'
import Maintenance from '@components/Maintenance'
import AccessDisabledModal from '@components/Modal/AccessDisabledModal'
import MaintenanceModal from '@components/Modal/Maintenance'
import PageOverlay from '@components/PageOverlay'
import withCurrentPermission, { PermissionsProps } from '@hocs/WithCurrentPermission'
import CustomPageMeta from '@hocs/WithPortalAuthorization/CustomMeta'
import { SessionProps, StoreProps } from '@interfaces/StoreState'
import { UserProfileProps } from '@interfaces/UserProfile'
import { PAGE } from '@utils/constants'
import { PROFILE_TYPE } from '@utils/constants'
import { formQueryString, getQueryParams } from '@utils/helpers'
import { getRoutingAdapter } from '@shell/adapters/routing'
import React from 'react'
import { connect } from 'react-redux'

interface WithPortalAuthorizationProps {
  id: string
  queryParams?: string[]
}

export interface WithPortalAuthorizationHOCProps extends StoreProps, SessionProps, PermissionsProps {
  loading?: boolean
  profile?: UserProfileProps
  isAdmin?: boolean
  roles: string[]
}

export default function WithPortalAuthorization(Component: any, params: WithPortalAuthorizationProps) {
  class WithPortalAuthorizationHOC extends React.Component<WithPortalAuthorizationHOCProps> {
    constructor(props) {
      super(props)
      this.checkPermissions = this.checkPermissions.bind(this)
      this.getPermittedComponent = this.getPermittedComponent.bind(this)
    }

    checkPermissions() {
      return {
        ...this.props.currentPermission,
        shouldDispay: this.props.validPermission,
      }
    }

    getPermittedComponent() {
      const { profile, isAdmin, roles, showSiteMaintenance } = this.props

      if (showSiteMaintenance) {
        return <MaintenanceModal open={showSiteMaintenance} />
      }

      if ((!profile?.SFId && !isAdmin) || !roles || !roles.length) {
        return <ErrorPlaceholder msg="User not recognized" />
      }

      const contractExpired =
        profile?.Type === PROFILE_TYPE.INSTRUCTOR &&
        (profile?.InstructorProfile?.instructorInfo?.ContractExpired ||
          profile?.InstructorProfile?.instructorInfo?.Status === 'Inactive')

      const studentProfileInactive =
        profile?.Type === PROFILE_TYPE.STUDENT && profile?.StudentProfile?.StudentProfileInfo?.Status === 'Inactive'

      if (studentProfileInactive) {
        const message = {
          header: 'Access disabled.',
          p1: 'Your student profile has been set as inactive.',
        }

        return <AccessDisabledModal open={studentProfileInactive} content={message} />
      }

      if (contractExpired) {
        return <AccessDisabledModal open={contractExpired} />
      }

      const pagePermission = this.checkPermissions()

      if (pagePermission.shouldDispay) {
        return <Component profile={profile} />
      }

      if (pagePermission.type !== PAGE.TYPES.PAGE || pagePermission.action === PAGE.ACTIONS.FORBIDDEN) {
        return <Error statusCode={403} />
      }

      if (pagePermission.action === PAGE.ACTIONS.MAINTENANCE) {
        return <Maintenance />
      }

      if (pagePermission.action === PAGE.ACTIONS.COMINGSOON) {
        return <ComingSoon />
      }

      if (pagePermission.action === PAGE.ACTIONS.SITEMAINTENANCE) {
        return <MaintenanceModal />
      }

      if (
        pagePermission.action === PAGE.ACTIONS.REDIRECTION &&
        pagePermission.redirections &&
        pagePermission.redirections.length
      ) {
        const redirections: string[] = []
        PAGE.ROLE_HIERARCHY.forEach((role) => {
          if (roles.includes(role)) {
            // @ts-ignore
            const redirect = pagePermission.redirections.find((redirection) => redirection.id === role)

            redirect && redirections.push(redirect.path)
          }
        })
        let redirectionBasedOnHierarchy = redirections[0]
        if (params.queryParams && Array.isArray(params.queryParams)) {
          const approvedParams = {}
          params.queryParams.map((query) => {
            const queryValue = getQueryParams(query, getRoutingAdapter().getCurrentRoute())
            if (queryValue) {
              approvedParams[query] = queryValue
            }
          })
          const queries = formQueryString(approvedParams)
          redirectionBasedOnHierarchy = queries
            ? `${redirectionBasedOnHierarchy}?${queries}`
            : redirectionBasedOnHierarchy
        }

        if (redirectionBasedOnHierarchy) {
          // TODO(PS-3321-follow-up): navigate() is async and may run guards — calling it
          // inside render() is a React anti-pattern. Move this redirect to
          // componentDidMount / useEffect to avoid state updates mid-render.
          void getRoutingAdapter().navigate(redirectionBasedOnHierarchy)
          return <PageOverlay />
        }
        return <Error statusCode={404} />
      }

      return <Error statusCode={404} />
    }

    render() {
      return <CustomPageMeta configId={params.id}>{this.getPermittedComponent()}</CustomPageMeta>
    }
  }

  const mapStateToProps = ({ session, userProfile }: StoreProps) => ({
    roles: session.cognitoGroups,
    user: session.user,
    loading: session.loading,
    isAdmin: session.isAdmin,
    profile: userProfile.info,
  })

  // @ts-ignore
  return withCurrentPermission(connect(mapStateToProps)(WithPortalAuthorizationHOC), {
    id: params.id,
    includeSiteMaintenance: true,
  })
}
